package com.checkin.host.ui.reservations

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.theme.*
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.host.ui.components.FormLabel
import com.checkin.host.ui.components.TextField
import com.checkin.host.ui.layout.rememberIsMobile
import com.checkin.host.util.downloadJsonFile
import com.checkin.host.util.openPdfInNewTab
import com.checkin.host.util.toBase64
import com.checkin.shared.dto.GuestContractFieldsDto
import com.checkin.shared.dto.GuestVerificationMediaDto
import com.checkin.shared.dto.ReservationDto
import com.checkin.shared.dto.ReservationVerificationMediaDto
import com.checkin.shared.dto.UpdateGuestContractFieldsRequest
import com.checkin.shared.dto.VerificationMediaFileDto
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun VerificationMediaModal(
    reservation: ReservationDto,
    onClose: () -> Unit,
    onUpdated: () -> Unit = {},
) {
    var loading by remember { mutableStateOf(true) }
    var message by remember { mutableStateOf<String?>(null) }
    var media by remember { mutableStateOf<ReservationVerificationMediaDto?>(null) }
    var fullscreen by remember { mutableStateOf<Pair<String, String>?>(null) }
    var reloadKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()

    fun loadMedia() {
        scope.launch {
            loading = true
            message = null
            try {
                media = ApiClient.listVerificationMedia(reservation.id)
            } catch (e: ApiException) {
                message = e.message
                media = null
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(reservation.id, reloadKey) { loadMedia() }

    fullscreen?.let { (url, fileName) ->
        FullscreenImageOverlay(url = url, fileName = fileName, onClose = { fullscreen = null })
    }

    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            backgroundColor(Color(AppColors.overlayScrim))
            display(DisplayStyle.Flex)
            alignItems(if (isMobile) AlignItems.FlexEnd else AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(if (isMobile) 0.px else 16.px)
        }
        onClick { onClose() }
    }) {
        Div({
            style {
                backgroundColor(AppColors.surface)
                borderRadius(16.px)
                width(100.percent)
                property("max-width", if (isMobile) "100%" else "760px")
                property("max-height", if (isMobile) "92vh" else "90vh")
                overflow("auto")
            }
            onClick { it.stopPropagation() }
        }) {
            Div({
                style {
                    padding(20.px, 24.px)
                    backgroundColor(AppColors.primary)
                    color(Color.white)
                }
            }) {
                H2({ style { margin(0.px) } }) { Text("Vérification du dossier") }
                P({ style { fontSize(13.px); marginTop(8.px); marginBottom(0.px) } }) {
                    Text("Documents, selfies et contrat signé — Réservation ${reservation.referenceManual}")
                }
            }
            Div({ style { padding(20.px) } }) {
                message?.let { AlertBanner(it, isError = !it.startsWith("✓")) }
                if (loading) {
                    P({ style { color(AppColors.muted) } }) { Text("Chargement...") }
                } else if (media == null && message == null) {
                    P({ style { color(AppColors.muted) } }) { Text("Impossible de charger le dossier.") }
                } else {
                    media?.guests?.forEach { guest ->
                        GuestMediaSection(
                            reservationId = reservation.id,
                            guest = guest,
                            onOpenFullscreen = { url, name -> fullscreen = url to name },
                            onApproved = {
                                reloadKey++
                                onUpdated()
                            },
                            onMessage = { message = it },
                        )
                    }
                }
                Button({ style { secondaryButton(); width(100.percent); marginTop(16.px) }; onClick { onClose() } }) {
                    Text("Fermer")
                }
            }
        }
    }
}

@Composable
private fun FullscreenImageOverlay(
    url: String,
    fileName: String?,
    onClose: () -> Unit,
) {
    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            backgroundColor(Color("rgba(0,0,0,0.92)"))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "2000")
            padding(16.px)
            cursor("zoom-out")
        }
        onClick { onClose() }
    }) {
        Img(src = url) {
            style {
                property("max-width", "100%")
                property("max-height", "calc(100vh - 80px)")
                property("object-fit", "contain")
                borderRadius(4.px)
            }
            onClick { it.stopPropagation() }
        }
        fileName?.let {
            P({ style { color(Color.white); fontSize(13.px); marginTop(12.px) } }) { Text(it) }
        }
        Button({
            style {
                marginTop(16.px)
                padding(10.px, 20.px)
                backgroundColor(AppColors.surface)
                color(Color.black)
                border(0.px)
                borderRadius(8.px)
                cursor("pointer")
                fontWeight("600")
            }
            onClick { it.stopPropagation(); onClose() }
        }) { Text("Fermer") }
    }
}

@Composable
private fun GuestMediaSection(
    reservationId: String,
    guest: GuestVerificationMediaDto,
    onOpenFullscreen: (url: String, fileName: String) -> Unit,
    onApproved: () -> Unit,
    onMessage: (String?) -> Unit,
) {
    val label = guest.guestName?.takeIf { it.isNotBlank() }
        ?: "Invité ${guest.guestIndex + 1}"
    val manual = if (guest.manualVerificationRequired) " · OCR à confirmer" else ""
    var approving by remember { mutableStateOf(false) }
    var resigning by remember { mutableStateOf(false) }
    val fields = guest.contractFields ?: GuestContractFieldsDto()
    var editName by remember(guest.guestId, fields.fullName) { mutableStateOf(fields.fullName) }
    var editIdNumber by remember(guest.guestId, fields.idNumber) { mutableStateOf(fields.idNumber) }
    var editIdExpiry by remember(guest.guestId, fields.idExpiry) { mutableStateOf(fields.idExpiry.orEmpty()) }
    var editNationality by remember(guest.guestId, fields.nationality) { mutableStateOf(fields.nationality.orEmpty()) }
    val scope = rememberCoroutineScope()

    Div({
        style {
            marginBottom(24.px)
            paddingBottom(20.px)
            property("border-bottom", "1px solid #e5e7eb")
        }
    }) {
        H3({ style { margin(0.px, 0.px, 12.px, 0.px); fontSize(16.px) } }) {
            Text("$label — ${guest.status.name.lowercase().replace('_', ' ')}$manual")
        }

        if (guest.awaitingHostReview) {
            Div({
                style {
                    padding(14.px)
                    backgroundColor(Color(AppColors.warningBg))
                    borderRadius(10.px)
                    border(1.px, LineStyle.Solid, Color(AppColors.warningBorder))
                    marginBottom(14.px)
                    fontSize(13.px)
                    lineHeight("1.5")
                    color(Color("#92400E"))
                }
            }) {
                Text(
                    "Contrat signé par l'occupant. Vérifiez les documents, le contrat et les données ci-dessous. " +
                        "Validez le dossier ou corrigez les informations et renvoyez un nouveau contrat à signer.",
                )
            }
        }

        MediaCategoryBlock(reservationId, guest.guestId, "Documents", guest.documents, onOpenFullscreen)
        MediaCategoryBlock(reservationId, guest.guestId, "Selfies", guest.selfies, onOpenFullscreen)

        if (guest.signedContractAvailable) {
            SignedContractBlock(reservationId, guest.guestId)
        }

        PersonalDataActions(
            reservationId = reservationId,
            guest = guest,
            onMessage = onMessage,
            onChanged = onApproved,
        )

        if (guest.contractFields != null) {
            Div({
                style {
                    marginTop(16.px)
                    padding(14.px)
                    backgroundColor(AppColors.surfaceHigh)
                    borderRadius(10.px)
                    border(1.px, LineStyle.Solid, AppColors.border)
                }
            }) {
                P({ style { margin(0.px, 0.px, 10.px, 0.px); fontWeight("600"); fontSize(14.px) } }) {
                    Text("Données du contrat")
                }
                Div({ style { marginBottom(10.px) } }) {
                    TextField("Nom complet", editName) { editName = it }
                }
                Div({ style { marginBottom(10.px) } }) {
                    TextField("Numéro de pièce", editIdNumber) { editIdNumber = it }
                }
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "1fr 1fr")
                        gap(10.px)
                        marginBottom(10.px)
                    }
                }) {
                    Div { TextField("Expiration pièce", editIdExpiry) { editIdExpiry = it } }
                    Div { TextField("Nationalité", editNationality) { editNationality = it } }
                }
            }
        }

        if (guest.manualVerificationRequired && !guest.awaitingHostReview) {
            Button({
                style {
                    padding(8.px, 14.px)
                    backgroundColor(AppColors.success)
                    color(Color.white)
                    border(0.px)
                    borderRadius(8.px)
                    cursor(if (approving) "wait" else "pointer")
                    fontWeight("600")
                    fontSize(13.px)
                    marginTop(8.px)
                    property("opacity", if (approving) "0.7" else "1")
                }
                if (!approving) {
                    onClick {
                        scope.launch {
                            approving = true
                            onMessage(null)
                            try {
                                ApiClient.approveManualVerification(reservationId, guest.guestId)
                                onMessage("✓ OCR confirmé pour $label")
                                onApproved()
                            } catch (e: ApiException) {
                                onMessage(e.message)
                            } finally {
                                approving = false
                            }
                        }
                    }
                }
            }) {
                Text(if (approving) "Enregistrement…" else "Confirmer lecture OCR")
            }
        }

        if (guest.awaitingHostReview) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    flexWrap(FlexWrap.Wrap)
                    marginTop(12.px)
                }
            }) {
                Button({
                    style {
                        padding(10.px, 16.px)
                        backgroundColor(AppColors.success)
                        color(Color.white)
                        border(0.px)
                        borderRadius(8.px)
                        cursor(if (approving) "wait" else "pointer")
                        fontWeight("600")
                        fontSize(13.px)
                        property("opacity", if (approving) "0.7" else "1")
                    }
                    if (!approving) {
                        onClick {
                            scope.launch {
                                approving = true
                                onMessage(null)
                                try {
                                    ApiClient.approveHostReview(reservationId, guest.guestId)
                                    onMessage("✓ Dossier validé pour $label")
                                    onApproved()
                                } catch (e: ApiException) {
                                    onMessage(e.message)
                                } finally {
                                    approving = false
                                }
                            }
                        }
                    }
                }) {
                    Text(if (approving) "Validation…" else "Valider le dossier")
                }
                Button({
                    style {
                        padding(10.px, 16.px)
                        backgroundColor(Color("#7c3aed"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(8.px)
                        cursor(if (resigning) "wait" else "pointer")
                        fontWeight("600")
                        fontSize(13.px)
                        property("opacity", if (resigning) "0.7" else "1")
                    }
                    if (!resigning) {
                        onClick {
                            scope.launch {
                                resigning = true
                                onMessage(null)
                                try {
                                    val link = ApiClient.requestContractResign(
                                        reservationId,
                                        guest.guestId,
                                        UpdateGuestContractFieldsRequest(
                                            fullName = editName.trim(),
                                            idNumber = editIdNumber.trim(),
                                            idExpiry = editIdExpiry.trim().takeIf { it.isNotEmpty() },
                                            nationality = editNationality.trim().takeIf { it.isNotEmpty() },
                                        ),
                                    )
                                    onMessage("✓ Nouveau contrat à signer — lien prêt pour $label")
                                    window.navigator.clipboard.writeText(link.url)
                                    onApproved()
                                } catch (e: ApiException) {
                                    onMessage(e.message)
                                } finally {
                                    resigning = false
                                }
                            }
                        }
                    }
                }) {
                    Text(if (resigning) "Envoi…" else "Corriger et renvoyer à signer")
                }
                Button({
                    style {
                        padding(10.px, 16.px)
                        secondaryButton()
                        fontSize(13.px)
                    }
                    onClick {
                        window.navigator.clipboard.writeText(guest.checkInUrl)
                        onMessage("✓ Lien check-in copié")
                    }
                }) { Text("Copier le lien") }
                Button({
                    style {
                        padding(10.px, 16.px)
                        secondaryButton()
                        fontSize(13.px)
                    }
                    onClick {
                        val msg = "Bonjour, merci de signer votre contrat mis à jour : ${guest.checkInUrl}"
                        window.open(
                            "https://wa.me/?text=${encodeURIComponent(msg)}",
                            "_blank",
                        )
                    }
                }) { Text("WhatsApp") }
            }
        }
    }
}

@Composable
private fun PersonalDataActions(
    reservationId: String,
    guest: GuestVerificationMediaDto,
    onMessage: (String?) -> Unit,
    onChanged: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var exporting by remember { mutableStateOf(false) }
    var purging by remember { mutableStateOf(false) }
    val hasData = guest.documents.isNotEmpty() ||
        guest.selfies.isNotEmpty() ||
        guest.signedContractAvailable ||
        !guest.verificationPurged
    if (!hasData && guest.verificationPurged && guest.contractPurged) return

    val exportJson = remember {
        Json {
            prettyPrint = true
            encodeDefaults = true
            ignoreUnknownKeys = true
        }
    }

    Div({
        style {
            marginTop(16.px)
            paddingTop(14.px)
            property("border-top", "1px solid #e5e7eb")
        }
    }) {
        P({ style { margin(0.px, 0.px, 10.px, 0.px); fontWeight("600"); fontSize(14.px) } }) {
            Text("Données personnelles (RGPD)")
        }
        if (guest.verificationPurged) {
            P({ style { fontSize(13.px); color(AppColors.muted); margin(0.px, 0.px, 10.px, 0.px) } }) {
                Text("Scans et selfies supprimés.")
            }
        }
        if (guest.contractPurged) {
            P({ style { fontSize(13.px); color(AppColors.muted); margin(0.px, 0.px, 10.px, 0.px) } }) {
                Text("Contrat signé supprimé.")
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Button({
                style {
                    secondaryButton()
                    fontSize(13.px)
                    property("opacity", if (exporting) "0.7" else "1")
                }
                if (!exporting) {
                    onClick {
                        scope.launch {
                            exporting = true
                            onMessage(null)
                            try {
                                val export = ApiClient.exportGuestPersonalData(reservationId, guest.guestId)
                                downloadJsonFile(
                                    "dossier-${guest.guestId}.json",
                                    exportJson.encodeToString(export),
                                )
                                onMessage("✓ Export téléchargé")
                            } catch (e: ApiException) {
                                onMessage(e.message)
                            } finally {
                                exporting = false
                            }
                        }
                    }
                }
            }) {
                Text(if (exporting) "Export…" else "Exporter le dossier")
            }
            if (!guest.verificationPurged || guest.signedContractAvailable) {
                Button({
                    style {
                        padding(10.px, 16.px)
                        backgroundColor(AppColors.danger)
                        color(Color.white)
                        border(0.px)
                        borderRadius(8.px)
                        cursor(if (purging) "wait" else "pointer")
                        fontWeight("600")
                        fontSize(13.px)
                        property("opacity", if (purging) "0.7" else "1")
                    }
                    if (!purging) {
                        onClick {
                            val confirmed = window.confirm(
                                "Supprimer définitivement les scans, selfies et le contrat signé de cet occupant ? " +
                                    "Cette action est irréversible.",
                            )
                            if (!confirmed) return@onClick
                            scope.launch {
                                purging = true
                                onMessage(null)
                                try {
                                    val result = ApiClient.purgeGuestPersonalData(reservationId, guest.guestId)
                                    onMessage("✓ ${result.message}")
                                    onChanged()
                                } catch (e: ApiException) {
                                    onMessage(e.message)
                                } finally {
                                    purging = false
                                }
                            }
                        }
                    }
                }) {
                    Text(if (purging) "Suppression…" else "Supprimer les données")
                }
            }
        }
    }
}

@Composable
private fun SignedContractBlock(reservationId: String, guestId: String) {
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div({ style { marginTop(12.px); marginBottom(12.px) } }) {
        FormLabel("Contrat signé")
        error?.let {
            P({ style { color(AppColors.danger); fontSize(13.px) } }) { Text(it) }
        }
        Button({
            style {
                secondaryButton()
                property("opacity", if (loading) "0.7" else "1")
            }
            if (!loading) {
                onClick {
                    scope.launch {
                        loading = true
                        error = null
                        try {
                            val bytes = ApiClient.signedContractPdf(reservationId, guestId)
                            openPdfInNewTab(bytes)
                        } catch (e: ApiException) {
                            error = e.message
                        } finally {
                            loading = false
                        }
                    }
                }
            }
        }) {
            Text(if (loading) "Ouverture…" else "Ouvrir le contrat signé (PDF)")
        }
    }
}

@Suppress("UnsafeCastFromDynamic")
private fun encodeURIComponent(value: String): String = js("encodeURIComponent")(value) as String

@Composable
private fun MediaCategoryBlock(
    reservationId: String,
    guestId: String,
    title: String,
    files: List<VerificationMediaFileDto>,
    onOpenFullscreen: (url: String, fileName: String) -> Unit,
) {
    if (files.isEmpty()) return
    P({ style { margin(0.px, 0.px, 8.px, 0.px); fontSize(13.px); color(AppColors.muted); fontWeight("600") } }) {
        Text(title)
    }
    val isMobile = rememberIsMobile()
    Div({
        style {
            display(DisplayStyle.Grid)
            property(
                "grid-template-columns",
                if (isMobile) "repeat(auto-fill, minmax(120px, 1fr))" else "repeat(auto-fill, minmax(140px, 1fr))",
            )
            gap(10.px)
            marginBottom(12.px)
        }
    }) {
        files.forEach { file ->
            MediaThumbnail(reservationId, guestId, file, onOpenFullscreen)
        }
    }
}

@Composable
private fun MediaThumbnail(
    reservationId: String,
    guestId: String,
    file: VerificationMediaFileDto,
    onOpenFullscreen: (url: String, fileName: String) -> Unit,
) {
    var dataUrl by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(file.storageKey) {
        scope.launch {
            try {
                val bytes = ApiClient.verificationMediaImage(reservationId, guestId, file.storageKey)
                dataUrl = "data:image/jpeg;base64,${bytes.toBase64()}"
            } catch (e: ApiException) {
                error = e.message
            } catch (_: Exception) {
                error = "Chargement impossible"
            }
        }
    }

    Div({
        style {
            border(1.px, LineStyle.Solid, Color("#e5e7eb"))
            borderRadius(8.px)
            overflow("hidden")
            backgroundColor(AppColors.surfaceHigh)
            if (dataUrl != null) {
                cursor("zoom-in")
            }
        }
        if (dataUrl != null) {
            onClick { onOpenFullscreen(dataUrl!!, file.fileName) }
        }
    }) {
        when {
            dataUrl != null -> Img(src = dataUrl!!) {
                style {
                    width(100.percent)
                    property("aspect-ratio", "4/3")
                    property("object-fit", "cover")
                    display(DisplayStyle.Block)
                }
            }
            error != null -> Div({ style { padding(12.px); fontSize(11.px); color(AppColors.danger) } }) {
                Text(error!!)
            }
            else -> Div({ style { padding(24.px); textAlign("center"); color(AppColors.muted); fontSize(12.px) } }) {
                Text("…")
            }
        }
        Div({ style { padding(6.px, 8.px); fontSize(11.px); color(AppColors.muted) } }) {
            Text(file.fileName)
        }
    }
}
